import { useEffect, useRef, useState } from 'react'
import { AxiosInstance } from 'axios'
import { useAuth0 } from 'src/contexts/auth0'
import { useAPIContext } from 'src/contexts/api'
import { diagnosePipelineURI, diagnoseContextURI } from 'src/contexts/api'
import { logError } from 'src/utils/Error'
import ndjsonStream from 'src/utils/stream/ndjson-stream'

//
// Drives the AI pipeline-failure diagnosis (MEL-574): POSTs to the diagnose
// endpoint and consumes the newline-delimited JSON stream, accumulating the
// model's tokens as they arrive. Mirrors the fetch + Auth0 token + ndjsonStream
// approach used by useJobLogs (the diagnose endpoint streams the same way the
// job-logs endpoint does).
//
// Stream events: { type: 'token', text } · { type: 'done', cached, fixType,
// jobId } · { type: 'error', error }. A 428 response means no LLM key is set.
//

export type DiagnosisStatus =
  | 'idle'
  | 'streaming'
  | 'done'
  | 'error'
  | 'no-key'
  | 'no-failure'

// mirrors the backend FIX_TYPE classification
export type FixType = 'PR' | 'SUPPORT' | 'INVESTIGATE' | 'UNKNOWN'

// How long to wait before revealing the "Diagnosing.." stepped checklist. Fast outcomes — a 428
// (no key) or a cache-hit 'done' — resolve under this and skip the checklist entirely, so it no
// longer flashes for a few ms before the promo / cached diagnosis (MEL-574 popup polish). A real
// fresh diagnosis is still in flight when this fires and shows the checklist as before.
const CHECKLIST_DELAY_MS = 500

export interface IDiagnosisState {
  status: DiagnosisStatus
  text: string
  fixType?: FixType
  cached?: boolean
  jobId?: string
  // backend error kind, e.g. 'invalid_key' | 'rate_limit' | 'timeout' | 'provider_error'
  error?: string
}

// The assembled input context (the actual prompt sent to Claude), fetched independently of the
// stream so it is available with or without an API key — powers the "Copy diagnosis prompt" button
// and the keyless copy path (MEL-574 demo feedback).
export interface IContextBundle {
  hasFailure: boolean
  prompt?: string
}

export interface IUseDiagnose extends IDiagnosisState {
  contextBundle?: IContextBundle
  // jobId is optional (MEL-574 per-run diagnosis): supply it to diagnose a specific run from the
  // history; omit it to diagnose the pipeline's most recent failure (the pipeline-level button).
  diagnose: (workspaceId: string, pipelineId: string, force?: boolean, jobId?: string) => Promise<void>
  // resolves with the assembled prompt (or undefined if there is none / the fetch failed) as well as
  // populating contextBundle, so a caller can copy the prompt on demand without pre-gating on load
  fetchContext: (workspaceId: string, pipelineId: string, jobId?: string) => Promise<string | undefined>
  reset: () => void
}

const useDiagnose = (): IUseDiagnose => {
  const { getTokenSilently } = useAuth0()
  const { catalogAPI } = useAPIContext()
  const api = catalogAPI as AxiosInstance

  const [state, setState] = useState<IDiagnosisState>({ status: 'idle', text: '' })
  const [contextBundle, setContextBundle] = useState<IContextBundle | undefined>(undefined)
  // guards against a late stream chunk updating state after unmount / reset
  const activeRef = useRef(false)

  useEffect(() => () => { activeRef.current = false }, [])

  const reset = () => {
    activeRef.current = false
    setState({ status: 'idle', text: '' })
    setContextBundle(undefined)
  }

  const diagnose = async (workspaceId: string, pipelineId: string, force = false, jobId?: string) => {
    activeRef.current = true
    // Start in the neutral 'idle' state (a plain spinner), NOT the "Diagnosing.." checklist.
    // The two fast-resolving outcomes — a 428 (no key → promo) and a cache-hit 'done'
    // ("Pipeline diagnosed") — both come back well under CHECKLIST_DELAY_MS, so neither flashes
    // the stepped checklist first (MEL-574 popup polish). Only a genuinely fresh diagnosis, where
    // the backend spends several seconds gathering context before the stream opens, is still
    // running when the timer fires — and only then does the checklist appear, with its steps
    // advancing exactly as before. The timer is cleared on any resolution (see below).
    setState({ status: 'idle', text: '' })
    let checklistTimer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
      // only escalate to the checklist if we're still waiting (idle) — a resolved state wins
      if (activeRef.current)
        setState(prev => (prev.status === 'idle' ? { status: 'streaming', text: '' } : prev))
    }, CHECKLIST_DELAY_MS)
    const clearChecklistTimer = () => {
      if (checklistTimer) { clearTimeout(checklistTimer); checklistTimer = undefined }
    }

    const base = (api.defaults.baseURL || '').replace(/\/$/, '')
    // diagnosePipelineURI may already carry a `?jobId=…` query, so pick the right separator for force
    const path = diagnosePipelineURI(workspaceId, pipelineId, jobId)
    const url = `${base}${path}${force ? `${path.includes('?') ? '&' : '?'}force=true` : ''}`

    try {
      const token = await getTokenSilently()
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/x-ndjson',
          Authorization: `Bearer ${token}`
        }
      })

      if (!activeRef.current) { clearChecklistTimer(); return }

      if (response.status === 428) {
        clearChecklistTimer()
        setState({ status: 'no-key', text: '' })
        return
      }
      if (response.status !== 200 || !response.body) {
        clearChecklistTimer()
        setState({ status: 'error', text: '', error: 'request_failed' })
        return
      }

      const reader = ndjsonStream(response.body).getReader()
      let text = ''

      while (true) {
        const result = await reader.read()
        if (result.done) break
        if (!activeRef.current) { clearChecklistTimer(); return }

        const event = result.value as any
        switch (event.type) {
          case 'token':
            // a real token stream — the checklist is (or now becomes) the right state
            clearChecklistTimer()
            text += event.text || ''
            setState({ status: 'streaming', text })
            break
          case 'done':
            clearChecklistTimer()
            if (event.hasFailure === false)
              setState({ status: 'no-failure', text: event.message || '' })
            else
              setState({
                status: 'done',
                text,
                fixType: event.fixType,
                cached: event.cached,
                jobId: event.jobId
              })
            break
          case 'error':
            clearChecklistTimer()
            setState({ status: 'error', text, error: event.error })
            break
          default:
            break
        }
      }
    } catch (e) {
      clearChecklistTimer()
      logError(e)
      if (activeRef.current)
        setState(prev => ({ ...prev, status: 'error', error: 'request_failed' }))
    }
  }

  // Fetches the assembled input context (the real prompt) for the pipeline's latest failure. Needs
  // no API key (the backend GET builds the context without an LLM call), so it works on the no-key
  // promo, the done state, and cache hits alike. Runs independently of the stream state above.
  const fetchContext = async (workspaceId: string, pipelineId: string, jobId?: string): Promise<string | undefined> => {
    activeRef.current = true
    const base = (api.defaults.baseURL || '').replace(/\/$/, '')
    const url = `${base}${diagnoseContextURI(workspaceId, pipelineId, jobId)}`

    try {
      const token = await getTokenSilently()
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      })
      if (response.status !== 200) return undefined
      const data = await response.json()
      // still populate state (unless we've been reset) so the modal can render, but always return
      // the prompt so an on-demand copy works even if the component has since unmounted
      if (activeRef.current)
        setContextBundle({ hasFailure: !!data.hasFailure, prompt: data.prompt })
      return data.prompt
    } catch (e) {
      logError(e)
      return undefined
    }
  }

  return { ...state, contextBundle, diagnose, fetchContext, reset }
}

export default useDiagnose
